import React, {createContext, useCallback, use, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {uuidv7} from "uuidv7";
import {
  getSessions,
  getMessages,
  deleteSession as deleteSessionApi,
  renameSession as renameSessionApi,
  setLastViewed as setLastViewedApi,
  setSessionPinned as setSessionPinnedApi,
  checkSessionActivity as checkSessionActivityApi,
  shareSession as shareSessionApi,
  revokeSessionShare as revokeSessionShareApi,
  leaveSession as leaveSessionApi,
  getConfirmation as getConfirmationApi,
  resolveConfirmation as resolveConfirmationApi,
} from "../../../api";
import {useUser} from "../../../components/user/Context/UserProvider";
import {useOrgSettings} from "../../../context/OrgSettingsProvider";
import {useAIServices} from "../../../context/AIServicesHooks";
import {SUPPORTED_IMAGE_MIMES, generatePastedImageName} from "../../../resources/utils/imageUtils";
import {downloadBlobAsFile} from "../../../resources/utils/files";
import useDocumentAttachments from "./useDocumentAttachments";
import useStoppedRequestRecovery from "./useStoppedRequestRecovery";
import useStreamHandler from "./useStreamHandler";
import {
  reconcileSessionMessages,
  toUiMessages,
} from "~/src/pages/RexAITrustedAdvisor/Context/sessionMessageHelpers";
import {
  buildConfirmationDecisions,
  getRestartRequiredMessage,
  isRestartRequired,
} from "./confirmationHelpers";
import {useRexAI} from "../../../context/RexAIProvider";
import {
  compact,
  every,
  filter,
  find,
  includes,
  isEqual,
  map,
  some,
  sortBy,
  toArray,
} from "underscore";

const RexAITrustedAdvisorContext = createContext();

const withAdmittedInputs = (data) =>
  sortBy(
    [
      ...(data.messages || []),
      ...map(data.admittedInputs || [], (input) => ({
        ...input,
        role: "user",
        content: input.content,
      })),
    ],
    (message) => message.createdAt || 0,
  );

const toUiSessionMessages = (data) =>
  toUiMessages(data?.messages ? withAdmittedInputs(data) : data || []);

const initialTokenCounts = {inputTokens: 0, outputTokens: 0, totalTokens: 0};

export const RexAITrustedAdvisorProvider = ({children}) => {
  const {apiClient} = useAIServices();
  const {user} = useUser();
  const orgSettings = useOrgSettings();
  const {responseWorkflowName, aiPermissions, trustedAdvisorModelProfile} = orgSettings;

  const [sessions, setSessions] = useState([]);
  const [sessionsCursor, setSessionsCursor] = useState(undefined);
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [isFetchingMoreSessions, setIsFetchingMoreSessions] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const {
    sessionId,
    setSessionId,
    currentPathname,
    messageContext,
    pendingWidgetMessage,
    setPendingWidgetMessage,
    setPendingAddPlaybook,
    formToolsEnabled,
  } = useRexAI();
  const [sessionBadges, setSessionBadges] = useState([]);
  const [currentRequest, setCurrentRequestState] = useState(null);
  const isCurrentRequest = !!currentRequest;
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [sharedConfirmationPending, setSharedConfirmationPending] = useState(false);
  const [sessionReadOnly, setSessionReadOnly] = useState(false);
  const [, setTokenCounts] = useState(initialTokenCounts);
  const [isThinking, setIsThinking] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [stoppedPromptToRestore, setStoppedPromptToRestore] = useState(null);
  const [skipSubscription, setSkipSubscription] = useState(false);

  const messageListRef = useRef(null);
  const fullResponseRef = useRef("");
  const fullThinkingRef = useRef("");
  const lastChunkIndexRef = useRef(-1);
  const inputRef = useRef(null);
  const currentAssistantMessageId = useRef(null);
  const stoppedRef = useRef(false);
  const streamAbortControllerRef = useRef(null);
  const activeInputRef = useRef(null);
  const retryInputRef = useRef(null);
  const viewVersionRef = useRef(0);

  const setCurrentRequest = useCallback((request) => {
    // Invalidate older recovery snapshots even after this newer request has finished.
    if (request) {
      viewVersionRef.current++;
    } else {
      activeInputRef.current = null;
    }
    setCurrentRequestState(request);
  }, []);

  const restoreUnconfirmedInput = useCallback((messageId) => {
    const activeInput = activeInputRef.current;
    if (!activeInput || activeInput.messageId !== messageId || activeInput.accepted) {
      return;
    }
    retryInputRef.current = activeInput;
    const draft = inputRef.current?.value;
    if (draft) {
      setStoppedPromptToRestore(activeInput.prompt);
    } else {
      activeInput.setInput(activeInput.prompt);
    }
    setMessages((prev) => filter(prev, (message) => message.messageId !== messageId));
  }, []);

  const clearRecoveredInput = useCallback((messageId) => {
    const retryInput = retryInputRef.current;
    if (retryInput?.messageId !== messageId) {
      return;
    }
    retryInputRef.current = null;
    setStoppedPromptToRestore(null);
    if (inputRef.current?.value === retryInput.prompt) {
      retryInput.setInput("");
    }
  }, []);

  const restoreStoppedPrompt = useCallback(() => {
    const retryInput = retryInputRef.current;
    if (!retryInput || !stoppedPromptToRestore) {
      return;
    }
    const currentDraft = inputRef.current?.value || "";
    retryInput.setInput(stoppedPromptToRestore);
    setStoppedPromptToRestore(currentDraft || null);
  }, [stoppedPromptToRestore]);

  const sessionIdRef = useRef(null);
  const openSessionIdRef = useRef(sessionId);
  const mountedRef = useRef(true);
  const backgroundRefreshInFlightRef = useRef(false);
  const lastBackgroundRefreshAtRef = useRef(0);
  openSessionIdRef.current = sessionId;

  const uploadFn = useCallback(
    (file, targetSessionId, signal) =>
      apiClient.uploadDocument({
        file,
        sessionId: targetSessionId,
        email: user.email,
        documentUploadEnabled: !!aiPermissions?.trustedAdvisorDocumentUpload,
        signal,
      }),
    [user?.email, aiPermissions?.trustedAdvisorDocumentUpload, apiClient],
  );

  const {
    pendingAttachments,
    maxAttachments,
    handleAttach: _handleAttach,
    removeAttachment,
    clearAttachments,
  } = useDocumentAttachments({uploadFn, onSessionCreated: setSessionId});

  const handleAttach = useCallback(
    (file) => _handleAttach(file, {sessionId, sessionIdRef}),
    [_handleAttach, sessionId],
  );

  const handlePaste = useCallback(
    async (event) => {
      const items = event.clipboardData?.files;
      if (items?.length) {
        const images = filter(toArray(items), (f) => SUPPORTED_IMAGE_MIMES.has(f.type));
        if (images.length) {
          event.preventDefault();
          const timestamp = Date.now();
          for (let i = 0; i < images.length; i++) {
            const file = images[i];
            const named = new File([file], generatePastedImageName(file, timestamp, i), {
              type: file.type,
            });
            await _handleAttach(named, {sessionId, sessionIdRef});
          }
          return;
        }
      }
      const text = `${event.clipboardData?.getData("text/plain") || ""}`
        .replace(/\r\n/g, "\n")
        .trimEnd();
      event.preventDefault();
      document.execCommand("insertText", false, text);
    },
    [_handleAttach, sessionId],
  );

  const sessionPageSizeRef = useRef(25);
  const sessionsCountRef = useRef(0);

  const fetchSessions = useCallback(
    async ({append = false, limit, cursor} = {}) => {
      if (!user?.email) {
        return;
      }
      try {
        const pageSize = limit || sessionPageSizeRef.current;
        // On refresh (non-append), re-fetch at least as many sessions as currently loaded
        // to avoid the list shrinking when background refreshes occur mid-scroll
        const refetchCount = append ? pageSize : Math.max(sessionsCountRef.current, pageSize);
        const data = await getSessions(apiClient, {
          email: user.email,
          limit: refetchCount,
          cursor: append ? cursor : undefined,
        });
        if (!mountedRef.current) {
          return;
        }

        if (data?.sessions) {
          if (append) {
            setSessions((prev) => {
              const updated = [...prev, ...data.sessions];
              sessionsCountRef.current = updated.length;
              return updated;
            });
          } else {
            setSessions(data.sessions);
            sessionsCountRef.current = data.sessions.length;
          }
          setSessionsCursor(data.nextCursor);
          setHasMoreSessions(data.hasMore);
        } else {
          setSessions(data || []);
          sessionsCountRef.current = (data || []).length;
          setSessionsCursor(undefined);
          setHasMoreSessions(false);
        }
      } catch (err) {
        if (!mountedRef.current) {
          return;
        }
        setError("Couldn't fetch your sessions. Please try again later.");
        console.error("Failed to fetch sessions:", err);
      }
    },
    [user?.email, apiClient],
  );

  const fetchMoreSessions = useCallback(async () => {
    if (!hasMoreSessions || isFetchingMoreSessions || !sessionsCursor) {
      return;
    }
    setIsFetchingMoreSessions(true);
    try {
      await fetchSessions({append: true, cursor: sessionsCursor});
    } finally {
      setIsFetchingMoreSessions(false);
    }
  }, [hasMoreSessions, isFetchingMoreSessions, sessionsCursor, fetchSessions]);

  const restoreSessionState = useCallback(
    async (targetSessionId, data, isCurrentView) => {
      if (!isCurrentView()) {
        return;
      }
      // Read access comes from the successful history lookup, not confirmation availability.
      setSessionReadOnly(!!data.readOnly);
      let confirmationData;
      try {
        confirmationData = await getConfirmationApi(apiClient, {sessionId: targetSessionId});
      } catch (error) {
        if (!isCurrentView()) {
          return;
        }
        // An unavailable lookup must not leave unverified approval controls active.
        setPendingConfirmation(null);
        setSharedConfirmationPending(false);
        throw error;
      }
      if (!isCurrentView()) {
        return;
      }
      const pending = confirmationData?.confirmations?.[0];
      setPendingConfirmation(
        pending ?
          {
            taskId: pending.agentTaskId,
            confirmationId: pending.confirmationId,
            confirmation: pending,
          } :
          null,
      );
      setSharedConfirmationPending(!!confirmationData?.hasPending);
      return {
        hasPending: !!pending || !!confirmationData?.hasPending,
        pendingSourceMessageId: pending?.runContext?.sourceMessageId,
      };
    },
    [apiClient],
  );

  const {refreshRequestOutcome, stopGeneration} = useStoppedRequestRecovery({
    apiClient,
    email: user?.email,
    sessionId,
    formatMessages: toUiSessionMessages,
    fetchSessions,
    restoreSessionState,
    restoreUnconfirmedInput,
    clearRecoveredInput,
    refs: {
      activeInputRef,
      currentAssistantMessageId,
      fullResponseRef,
      inputRef,
      lastChunkIndexRef,
      mountedRef,
      openSessionIdRef,
      sessionIdRef,
      stoppedRef,
      streamAbortControllerRef,
      viewVersionRef,
    },
    actions: {
      setCurrentRequest,
      setError,
      setIsStopping,
      setIsThinking,
      setMessages,
      setSessionId,
      setSkipSubscription,
    },
  });

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const onPageHide = () => {
      mountedRef.current = false;
    };
    const onPageShow = () => {
      mountedRef.current = true;
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      clearAttachments();
    };
  }, [clearAttachments]);

  useStreamHandler(apiClient, {
    currentRequest,
    sessionId,
    skipSubscription,
    user,
    aiPermissions,
    trustedAdvisorModelProfile,
    responseWorkflowName,
    refs: {
      stoppedRef,
      streamAbortControllerRef,
      sessionIdRef,
      fullResponseRef,
      fullThinkingRef,
      lastChunkIndexRef,
      currentAssistantMessageId,
    },
    setMessages,
    setError,
    setSessionId,
    setCurrentRequest,
    setIsThinking,
    setTokenCounts,
    setPendingConfirmation,
    fetchSessions,
    onRequestPrepared: (request) => {
      if (activeInputRef.current?.messageId === request.messageId) {
        activeInputRef.current.request = request;
      }
    },
    onInputAccepted: (messageId) => {
      if (activeInputRef.current?.messageId !== messageId) {
        return;
      }
      activeInputRef.current.accepted = true;
      retryInputRef.current = null;
      setStoppedPromptToRestore(null);
      if (!activeInputRef.current.widgetMessage) {
        clearAttachments();
      }
    },
    onInputNotAccepted: restoreUnconfirmedInput,
    onInputAlreadyAccepted: (payload) =>
      refreshRequestOutcome(payload.sessionId, payload.messageId),
    onDepartureRestored: (departure) => stopGeneration({departure}),
  });

  useEffect(() => {
    if (!user?.email) {
      return;
    }

    let active = true;
    const pollActivity = async () => {
      try {
        const data = await checkSessionActivityApi(apiClient, {email: user.email, sessionId});
        if (!active || !mountedRef.current) {
          return;
        }
        if (data?.sessionsWithNewActivity) {
          const incoming = data.sessionsWithNewActivity;
          setSessionBadges((prev) => {
            if (prev?.length === incoming?.length && every(prev, (id, i) => id === incoming[i])) {
              return prev;
            }
            return incoming;
          });

          setSessions((current) => {
            const knownIds = map(current, (s) => s.sessionId);
            if (some(incoming, (id) => !includes(knownIds, id))) {
              fetchSessions();
            }
            return current;
          });
        }
      } catch (err) {
        if (!active || !mountedRef.current) {
          return;
        }
        console.error("Failed to check session activity:", err);
      }
    };

    const timeout = setTimeout(pollActivity, 500);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [user?.email, sessionId, isThinking, apiClient, fetchSessions]);

  const handleConfirmation = useCallback(
    async (approved, itemDecisions) => {
      if (!(pendingConfirmation && user?.email && sessionId)) {
        return;
      }

      setError("");

      try {
        const decisions = buildConfirmationDecisions(
          pendingConfirmation.confirmation.items,
          approved,
          itemDecisions,
        );

        await resolveConfirmationApi(apiClient, {
          sessionId,
          email: user.email,
          confirmationId: pendingConfirmation.confirmationId,
          items: decisions,
        });
        if (!approved) {
          const deniedItems = map(pendingConfirmation.confirmation.items, (item) => ({
            ...item,
            status: "denied",
          }));
          setMessages((prev) => [
            ...prev,
            {
              messageId: uuidv7(),
              text: "🚫 Operations cancelled.",
              sender: "assistant",
              createdAt: Date.now(),
              confirmationResult: {
                items: deniedItems,
                resolvedBy: user.email,
              },
            },
          ]);
          setPendingConfirmation(null);
          return;
        }

        setPendingConfirmation(null);
        stoppedRef.current = false;
        setSkipSubscription(false);
        fullResponseRef.current = "";
        setIsThinking(true);
        const resumeRequestId = uuidv7();
        activeInputRef.current = {
          messageId: resumeRequestId,
          agentTaskId: pendingConfirmation.taskId,
          confirmationId: pendingConfirmation.confirmationId,
          accepted: true,
        };
        setCurrentRequest({
          prompt: null,
          messageId: resumeRequestId,
          agentTaskId: pendingConfirmation.taskId,
          confirmationId: pendingConfirmation.confirmationId,
          sessionId,
        });
      } catch (err) {
        console.error("Confirmation failed:", err);
        if (isRestartRequired(err)) {
          setPendingConfirmation(null);
          setError(getRestartRequiredMessage(err));
        } else {
          setError("Failed to process confirmation. Please try again.");
        }
      }
    },
    [pendingConfirmation, user?.email, sessionId, apiClient],
  );

  const handleSend = useCallback(
    async (e, input, setInput = () => {}) => {
      e?.preventDefault();
      if (!input.trim()) {
        return;
      }
      if (isCurrentRequest || activeInputRef.current) {
        setError("Maestro is still responding. Stop that response before sending another message.");
        return;
      }

      if (pendingConfirmation) {
        const normalized = input.trim().toLowerCase();
        if (normalized === "y" || normalized === "yes") {
          setInput("");
          await handleConfirmation(true);
          return;
        } else if (normalized === "n" || normalized === "no") {
          setInput("");
          await handleConfirmation(false);
          return;
        }
        setError("Please respond with \"y\" or \"n\", or click Approve/Cancel.");
        return;
      }

      stoppedRef.current = false;
      setSkipSubscription(false);
      const widgetFields =
        !e && pendingWidgetMessage?.text === input && pendingWidgetMessage.widgetType ?
          {
            widgetType: pendingWidgetMessage.widgetType,
            widgetValue: pendingWidgetMessage.widgetValue,
            widgetLabel: pendingWidgetMessage.widgetLabel,
          } :
          null;
      const readyAttachments = widgetFields ?
        [] :
        filter(
          pendingAttachments,
          (attachment) => attachment.docId && attachment.status !== "error",
        );
      const retryInput =
        retryInputRef.current?.prompt === input &&
        isEqual(retryInputRef.current.attachments, readyAttachments) ?
          retryInputRef.current :
          null;
      const retainedDraft =
        retryInput && stoppedPromptToRestore !== retryInput.prompt ? stoppedPromptToRestore : "";
      const messageId = retryInput?.messageId || uuidv7();
      const userMessage = {
        messageId,
        text: input,
        sender: "user",
        createdAt: Date.now(),
        ...(readyAttachments.length > 0 && {attachments: readyAttachments}),
        ...widgetFields,
      };
      const userPrompt = input;
      const docIds = compact(map(readyAttachments, (attachment) => attachment.docId));
      activeInputRef.current = {
        messageId,
        prompt: userPrompt,
        attachments: readyAttachments,
        setInput,
        accepted: false,
        request: retryInput?.request,
        widgetMessage: Boolean(widgetFields),
      };
      retryInputRef.current = null;
      setStoppedPromptToRestore(null);
      setMessages((prev) => [...prev, userMessage]);
      setInput(retainedDraft || "");
      setError("");
      fullResponseRef.current = "";
      setIsThinking(true);
      setCurrentRequest({
        prompt: userPrompt,
        messageId,
        ...(docIds.length > 0 && {docIds}),
        ...(readyAttachments.length > 0 && {attachments: readyAttachments}),
        ...(retryInput?.request && {frozenRequest: retryInput.request}),
        ...widgetFields,
      });
      if (widgetFields) {
        setPendingWidgetMessage(null);
      }
    },
    [
      isCurrentRequest,
      pendingConfirmation,
      handleConfirmation,
      pendingAttachments,
      pendingWidgetMessage,
      stoppedPromptToRestore,
      setPendingWidgetMessage,
    ],
  );

  useEffect(() => {
    if (
      !pendingWidgetMessage ||
      isCurrentRequest ||
      activeInputRef.current ||
      pendingConfirmation
    ) {
      return;
    }
    const {text, awaitRoute} = pendingWidgetMessage;
    if (awaitRoute && messageContext?.toolMode !== "addForm") {
      return;
    }
    if (awaitRoute && messageContext?.pageType !== "incident") {
      return;
    }
    if (awaitRoute && currentPathname !== awaitRoute) {
      return;
    }
    if (awaitRoute && !formToolsEnabled) {
      return;
    }
    handleSend(null, text);
  }, [
    pendingWidgetMessage,
    isCurrentRequest,
    pendingConfirmation,
    messageContext,
    currentPathname,
    formToolsEnabled,
    handleSend,
  ]);

  const loadSession = useCallback(
    async (targetSessionId) => {
      if (!user?.email) {
        return;
      }
      setPendingWidgetMessage(null);
      setPendingAddPlaybook(null);
      const expectedViewVersion = ++viewVersionRef.current;
      const isCurrentView = () =>
        mountedRef.current && viewVersionRef.current === expectedViewVersion;

      try {
        setCurrentRequest(null);
        retryInputRef.current = null;
        setStoppedPromptToRestore(null);

        setSessionBadges((prev) => {
          if (!includes(prev, targetSessionId)) {
            return prev;
          }
          return filter(prev, (id) => id !== targetSessionId);
        });

        const data = await getMessages(apiClient, {sessionId: targetSessionId, email: user.email});
        if (!isCurrentView()) {
          return;
        }
        if (!data) {
          setError("Failed to load session messages");
          return;
        }

        const isGeneratingSessionName = data.isGeneratingName;
        setSessionReadOnly(!!data.readOnly);

        const loadedMessages = toUiSessionMessages(data);
        setMessages(loadedMessages);
        setSessionId(targetSessionId);
        console.log("Switched to session:", targetSessionId);
        setCurrentRequest(null);
        setError("");
        clearAttachments();
        fullResponseRef.current = "";
        currentAssistantMessageId.current = null;
        setIsThinking(false);

        try {
          await restoreSessionState(targetSessionId, data, isCurrentView);
        } catch (err) {
          if (!isCurrentView()) {
            return;
          }
          console.error("Failed to restore pending confirmation:", err);
          setPendingConfirmation(null);
          setSharedConfirmationPending(false);
        }
        if (!isCurrentView()) {
          return;
        }

        try {
          await setLastViewedApi(apiClient, {sessionId: targetSessionId, email: user.email});
        } catch (err) {
          console.error("Failed to set lastViewed:", err);
        }

        setTimeout(() => inputRef.current?.focus(), 0);

        if (isGeneratingSessionName) {
          setTimeout(fetchSessions, 3000);
        }
      } catch (err) {
        if (!isCurrentView()) {
          return;
        }
        console.error("Error loading session:", err);
        setError("Failed to load session");
      }
    },
    [
      user?.email,
      fetchSessions,
      apiClient,
      restoreSessionState,
      setPendingWidgetMessage,
      setPendingAddPlaybook,
    ],
  );

  const createNewSession = useCallback(() => {
    viewVersionRef.current++;
    setSessionId(null);
    sessionIdRef.current = null;
    setMessages([]);
    setCurrentRequest(null);
    retryInputRef.current = null;
    setStoppedPromptToRestore(null);
    setError("");
    setTokenCounts(initialTokenCounts);
    setIsThinking(false);
    setPendingConfirmation(null);
    setSharedConfirmationPending(false);
    setSessionReadOnly(false);
    clearAttachments();
    fullResponseRef.current = "";
    currentAssistantMessageId.current = null;

    setTimeout(() => inputRef.current?.focus(), 0);
  }, [setSessionId]);

  const refreshOpenSession = useCallback(
    async (targetSessionId) => {
      if (!user?.email) {
        return;
      }
      const expectedViewVersion = viewVersionRef.current;
      const isCurrentView = () =>
        mountedRef.current &&
        viewVersionRef.current === expectedViewVersion &&
        openSessionIdRef.current === targetSessionId;

      try {
        const data = await getMessages(apiClient, {sessionId: targetSessionId, email: user.email});
        if (!data || !isCurrentView()) {
          return;
        }

        setMessages((previous) => reconcileSessionMessages(previous, toUiSessionMessages(data)));
        setSessionReadOnly(!!data.readOnly);

        try {
          await restoreSessionState(targetSessionId, data, isCurrentView);
        } catch (err) {
          if (!isCurrentView()) {
            return;
          }
          console.error("Failed to refresh pending confirmation:", err);
        }
        if (!isCurrentView()) {
          return;
        }

        try {
          await setLastViewedApi(apiClient, {sessionId: targetSessionId, email: user.email});
        } catch (err) {
          console.error("Failed to refresh lastViewed:", err);
        }
      } catch (err) {
        if (!isCurrentView()) {
          return;
        }
        if (err.status === 403 || err.status === 404) {
          createNewSession();
          return;
        }
        console.error("Failed to refresh session:", err);
        setError("Failed to refresh session");
      }
    },
    [apiClient, createNewSession, user?.email, restoreSessionState],
  );

  useEffect(() => {
    const refreshOnReturn = async () => {
      if (document.visibilityState === "hidden" || backgroundRefreshInFlightRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastBackgroundRefreshAtRef.current < 250) {
        return;
      }
      lastBackgroundRefreshAtRef.current = now;
      backgroundRefreshInFlightRef.current = true;

      try {
        const currentSessionExists = some(sessions, (session) => session.sessionId === sessionId);
        const refreshes = [fetchSessions()];
        if (sessionId && currentSessionExists && !isCurrentRequest) {
          refreshes.push(refreshOpenSession(sessionId));
        }
        await Promise.all(refreshes);
      } finally {
        backgroundRefreshInFlightRef.current = false;
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshOnReturn();
      }
    };

    window.addEventListener("focus", refreshOnReturn);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [fetchSessions, isCurrentRequest, refreshOpenSession, sessionId, sessions]);

  const deleteSession = useCallback(
    async (targetSessionId) => {
      if (!user?.email) {
        return;
      }

      try {
        await deleteSessionApi(apiClient, {sessionId: targetSessionId, email: user.email});

        if (targetSessionId === sessionId) {
          createNewSession();
        }

        await fetchSessions();
      } catch (err) {
        console.error("Error deleting session:", err);
        setError("Failed to delete session");
      }
    },
    [user?.email, sessionId, createNewSession, fetchSessions, apiClient],
  );

  const renameSession = useCallback(
    async (targetSessionId, newName) => {
      if (!user?.email) {
        return;
      }

      try {
        await renameSessionApi(apiClient, {
          sessionId: targetSessionId,
          sessionName: newName,
          email: user.email,
        });

        setSessions((prev) =>
          map(prev, (session) =>
            session.sessionId === targetSessionId ? {...session, sessionName: newName} : session,
          ),
        );
      } catch (err) {
        console.error("Error renaming session:", err);
        setError("Failed to rename session");
      }
    },
    [user?.email, apiClient],
  );

  const setSessionPinned = useCallback(
    async (targetSessionId, pinned) => {
      try {
        await setSessionPinnedApi(apiClient, {sessionId: targetSessionId, pinned});
        await fetchSessions();
      } catch (err) {
        console.error("Error updating session pin:", err);
        setError(`Failed to ${pinned ? "pin" : "unpin"} session`);
      }
    },
    [apiClient, fetchSessions],
  );

  const shareSession = useCallback(
    async (targetSessionId, userIds) => {
      const result = await shareSessionApi(apiClient, {sessionId: targetSessionId, userIds});
      await fetchSessions();
      return result;
    },
    [apiClient, fetchSessions],
  );

  const revokeSessionShare = useCallback(
    async (targetSessionId, userId) => {
      await revokeSessionShareApi(apiClient, {sessionId: targetSessionId, userId});
      await fetchSessions();
    },
    [apiClient, fetchSessions],
  );

  const leaveSession = useCallback(
    async (targetSessionId) => {
      try {
        await leaveSessionApi(apiClient, {sessionId: targetSessionId});
        if (targetSessionId === sessionId) {
          createNewSession();
        }
        await fetchSessions();
      } catch (err) {
        console.error("Error leaving shared session:", err);
        setError("Failed to leave shared session");
      }
    },
    [apiClient, createNewSession, fetchSessions, sessionId],
  );

  const isAnyUploading = some(
    pendingAttachments,
    (pendingAttachment) => pendingAttachment.status === "uploading",
  );

  const downloadDocument = useCallback(
    async (docId, fileName) => {
      if (!sessionId) {
        return;
      }
      try {
        const {url} = await apiClient.post(
          `/v1/maestro/sessions/${sessionId}/documents/${docId}/download`,
          {},
        );
        const response = await fetch(url);
        const blob = await response.blob();
        downloadBlobAsFile(blob, fileName);
      } catch (err) {
        console.error("Failed to download document:", err);
        setError("Failed to download document. Please try again.");
      }
    },
    [sessionId, apiClient],
  );

  const currentSession = find(sessions, (session) => session.sessionId === sessionId);
  const providerValues = {
    messages,
    error,
    loading: isThinking,
    sessionId,
    isThinking,
    isStopping,
    stoppedPromptToRestore,
    stoppedPromptRestoreLabel:
      stoppedPromptToRestore === retryInputRef.current?.prompt ?
        "Restore stopped prompt" :
        "Restore draft",
    isCurrentRequest,
    sessions,
    currentSession,
    isReadOnly: sessionReadOnly || !!currentSession?.shared,
    sharedConfirmationPending,
    sessionBadges,
    hasMoreSessions,
    isFetchingMoreSessions,
    fetchMoreSessions,
    fetchSessions,
    sessionPageSizeRef,
    documentUploadEnabled: !!aiPermissions?.trustedAdvisorDocumentUpload,
    isDocUploading: isAnyUploading,
    pendingAttachments,
    maxAttachments,
    pendingConfirmation,
    handleAttach,
    handlePaste,
    handleSend,
    handleConfirmation,
    removeAttachment,
    loadSession,
    createNewSession,
    deleteSession,
    renameSession,
    setSessionPinned,
    shareSession,
    revokeSessionShare,
    leaveSession,
    stopGeneration,
    restoreStoppedPrompt,
    downloadDocument,
    messageListRef,
    inputRef,
  };

  return <RexAITrustedAdvisorContext value={providerValues}>{children}</RexAITrustedAdvisorContext>;
};

RexAITrustedAdvisorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useRexAITrustedAdvisorContext = () => {
  const context = use(RexAITrustedAdvisorContext);

  if (!context) {
    throw new Error("Context must be used within the correct provider");
  }
  return context;
};
